import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineL } from '#src/atoms/typography/index.tsx';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { RECOVERY_EMAIL_VERIFICATION_CODE_LENGTH } from '#src/constants/verificationCode.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useActivateWalletRecoveryEmail } from '#src/hooks/recovery/useActivateWalletRecoveryEmail.ts';
import { glossary } from '#src/lib/glossary.ts';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
  form {
    margin-bottom: 0;
  }
`;
const StyledTextField = styled(TextField)`
  width: 100%;
`;

type Props = {
  email: string;
  onSuccess: () => void;
};
export const VerifyRecoveryEmailForm = ({
  email,
  onSuccess: doOnSuccess,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { showNotification } = useSnackNotificationContext();
  const { activateWalletRecoveryEmail } = useActivateWalletRecoveryEmail();
  const submit = async (values: any, onResult: (r: GraphQLResult) => void) => {
    const results = await activateWalletRecoveryEmail({
      destination: email,
      verificationCode: values.verificationCode,
    });
    onResult(results);
  };

  const onSuccess = () => {
    showNotification('recoveryEmailRegistered', { email });
    doOnSuccess();
  };

  if (!currentUser) return null;

  return (
    <Wrapper>
      <Vertical gap={2}>
        <HeadlineL as="h3" bold>
          <FormattedMessage defaultMessage="Verify your recovery email" />
        </HeadlineL>
        <BodyM>
          <FormattedMessage
            defaultMessage="Enter the 6-digit code sent to { email }."
            values={{ email }}
          />
        </BodyM>
      </Vertical>
      <GraphqlForm
        onSubmit={(values, doOnResult) => {
          submit(values, doOnResult);
        }}
        onChange={(values, doSubmit) => {
          const { verifiactionCode } = values;
          if (
            verifiactionCode?.length === RECOVERY_EMAIL_VERIFICATION_CODE_LENGTH
          )
            doSubmit();
        }}
        onSuccess={onSuccess}
        render={(Error, SubmitButton) => (
          <Vertical>
            <Error code />
            <StyledTextField name="verificationCode" />
            <SubmitButton fullWidth size="large">
              <FormattedMessage {...glossary.verify} />
            </SubmitButton>
          </Vertical>
        )}
      />
    </Wrapper>
  );
};
